/* ============================================================================
   Hemlock & Oak — custom.js
   ----------------------------------------------------------------------------
   Site-wide custom JavaScript, moved out of theme.liquid so it:
     - is cached by the browser (downloaded once, not re-shipped on every page)
     - survives theme updates (it is NOT inside the vendored theme.js bundle)
     - keeps theme.liquid readable

   Loaded from theme.liquid <head> with:
     <script src="{{ 'custom.js' | asset_url }}" defer></script>

   This is a CLASSIC (non-module) script on purpose. `defer` makes it
   non-blocking, while classic scope keeps the one deliberate global these
   snippets rely on (window.showVariantMediaInGallery, called from
   product-card markup) working exactly as it did inline. A module would seal
   that scope and break the reference.

   THE WHOLE FILE IS ONE IIFE — never add top-level const/let/class OUTSIDE it.
   WHY: in a classic script a top-level class/let/const becomes a GLOBAL
   lexical binding shared by every other classic script on the page.
   sections/cart-drawer.liquid still renders an inline copy of the cart-recs
   block, so `class CartRecsCarousel` already exists globally before this
   deferred file runs; when this file also declared it at top level the engine
   threw "Identifier 'CartRecsCarousel' has already been declared" BEFORE
   executing a single statement, silently killing EVERY block in this file.
   That is exactly how the Judge.me stars vanished on 2026-07-23: the stars'
   only surviving paint code lived here and the whole file was dead on
   arrival. Reordering blocks could never fix that (the abort happens
   pre-execution) — only removing all top-level lexical bindings does, hence
   the wrapper. Inside the IIFE every declaration is function-scoped, so no
   name collision with any other script is possible in either direction.

   Contents (the star restyle stays FIRST inside the IIFE so a runtime throw
   in a later block can never stop the stars from painting):
     1. Judge.me star restyle + Sezzle "or" text fix
     2. Product card interactions
     3. Swatch "show more" button
     4. Quick add-to-cart (.tm-add-bag) with animation
     5. Swatch change -> variant name label
     6. window.showVariantMediaInGallery (swatch hover -> gallery image)
     7. Cart recommendations carousel (inline twin in cart-drawer.liquid —
        guarded so both copies coexist; see the block note)
     8. Sold-out product card fade
     9. Tepo customizer widget mover
    10. Duo pair integrity (cart-wide orphan enforcement + load sweep)
   ========================================================================== */
(function () {

/* ----------------------------------------------------------------------------
   1. Judge.me star restyle + Sezzle "or" text fix

   replaceStars(): swaps Judge.me's star glyphs for your custom SVG.
   fixSezzle():    strips the leading "or " from the Sezzle button text.

   NOTE 1 (Sezzle): if you ever drop Sezzle, delete the fixSezzle() function and
     the `if (!sezzleDone) ...` line in the observer below — that's the whole
     Sezzle piece. Everything else here is Judge.me.

   NOTE 2 (perf): the observer scans the page on every DOM change and never
     disconnects, to catch stars that load asynchronously. It works, but it's
     the one block worth optimizing later. Kept faithful here on purpose.
   -------------------------------------------------------------------------- */
(function () {
  var STAR_PATH = 'M283.84 867.84 512 747.776l228.16 119.936a6.4 6.4 0 0 0 9.28-6.72l-43.52-254.08 184.512-179.904a6.4 6.4 0 0 0-3.52-10.88l-255.104-37.12L517.76 147.904a6.4 6.4 0 0 0-11.52 0L392.192 379.072l-255.104 37.12a6.4 6.4 0 0 0-3.52 10.88L318.08 606.976l-43.584 254.08a6.4 6.4 0 0 0 9.28 6.72z';
  var STAR_ON = "#ca8b38";
  var STAR_OFF = "#d9d9d9";

  /* Judge.me marks each star jdgm--on / jdgm--half / jdgm--off. A half star is
     drawn as the empty star with a bronze copy clipped to its left half —
     clip-path instead of an SVG gradient so no <defs> ids can collide when the
     same markup repeats per star. */
  function starSVG(state) {
    var paths;
    if (state === "half") {
      paths =
        '<path fill="' + STAR_OFF + '" d="' + STAR_PATH + '"/>' +
        '<path fill="' + STAR_ON + '" style="clip-path:inset(0 50% 0 0)" d="' + STAR_PATH + '"/>';
    } else {
      paths = '<path fill="' + (state === "on" ? STAR_ON : STAR_OFF) + '" d="' + STAR_PATH + '"/>';
    }
    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024" width="16" height="16">' + paths + '</svg>';
  }

  function replaceStars() {
    var stars = document.querySelectorAll(".jdgm-star:not([data-star-replaced])");
    stars.forEach(function (star) {
      try {
      var state = "off";
      if (star.classList.contains("jdgm--on")) state = "on";
      else if (star.classList.contains("jdgm--half")) state = "half";
      star.innerHTML = starSVG(state);
      star.setAttribute("data-star-replaced", "true");
      star.style.setProperty("display", "inline-block", "important");
      star.style.setProperty("line-height", "0", "important");
      star.style.setProperty("font-size", "0", "important");
      star.style.setProperty("margin", "0", "important");
      star.style.setProperty("padding", "0", "important");
      } catch (e) { /* never let one node kill the pass */ }
    });
  }

  function fixSezzle() {
    var el = document.querySelector(".sezzle-button-text");
    if (el && el.firstChild && el.firstChild.nodeType === 3) {
      var txt = el.firstChild.textContent;
      if (txt.indexOf("or ") === 0) {
        el.firstChild.textContent = txt.replace(/^or /, "");
        return true;
      }
    }
    return false;
  }

  var sezzleDone = false;
  function runAll() {
    replaceStars();
    if (!sezzleDone) sezzleDone = fixSezzle();
  }
  var observer = new MutationObserver(runAll);
  function start() {
    runAll(); /* server HTML already carries jdgm--on/half classes — paint immediately */
    observer.observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();


/* ----------------------------------------------------------------------------
   2. Product card interactions
   Shared handlers extracted from product-card and product-card-variant-display.
   -------------------------------------------------------------------------- */
document.addEventListener("change", function (e) {
  const radio = e.target;
  if (!(radio instanceof HTMLInputElement)) return;
  if (!radio.matches('.product-card__swatch-list [type="radio"], .product-card__variant-list [type="radio"]')) return;
  if (!radio.dataset.variantId) return;

  const card = radio.closest("product-card");
  if (!card) return;

  const hiddenInput = card.querySelector('.product-card__quick-buy form[is="product-form"] input[name="id"]');
  if (hiddenInput) {
    hiddenInput.value = radio.dataset.variantId;
  }
});

document.addEventListener("click", function (e) {
  const target = e.target;
  if (!(target instanceof Element)) return;

  const showMoreBtn = target.closest(".show-more-button");
  if (!showMoreBtn) return;

  const swatchList = showMoreBtn.closest(".product-card__swatch-list");
  if (!swatchList) return;

  e.preventDefault();
  e.stopPropagation();

  swatchList.querySelectorAll(".hidden-swatch").forEach((swatch) => {
    swatch.style.display = "inline-flex";
  });
  showMoreBtn.remove();
}, true);

document.addEventListener("click", function (e) {
  const target = e.target;
  if (!(target instanceof Element)) return;

  const card = target.closest("variant-card");
  if (!card) return;
  if (target.closest("button") || target.closest("a") || target.closest("input")) return;

  const variantId = card.getAttribute("variant-id");
  const handle = card.getAttribute("handle");
  if (handle && variantId) {
    window.location.href = `/products/${handle}?variant=${variantId}`;
  }
});


/* ----------------------------------------------------------------------------
   3. Swatch "show more" button
   Clicking the "+N" button on a product card reveals the hidden swatches.
   -------------------------------------------------------------------------- */
document.addEventListener("DOMContentLoaded", function () {
  document.addEventListener("click", function (e) {
    const showMoreBtn = e.target.closest(".tm-show-more-button");
    if (showMoreBtn) {
      e.preventDefault();
      e.stopPropagation();
      const swatchList = showMoreBtn.closest(".product-card__swatch-list");
      if (swatchList) {
        swatchList.querySelectorAll(".hidden-swatch").forEach((swatch) => {
          swatch.style.display = "inline-flex";
        });
      }
      const wrapper = showMoreBtn.closest(".upsell-swatch-wrapper");
      if (wrapper) {
        wrapper.classList.add("force-scrollbar");
      }
      showMoreBtn.remove();
      return;
    }
  });
});


/* ----------------------------------------------------------------------------
   4. Quick add-to-cart (.tm-add-bag) with animation
   AJAX-adds the variant, runs the loading -> full -> success animation, and
   dispatches the theme's cart events so the cart drawer / count update.
   -------------------------------------------------------------------------- */
document.addEventListener("DOMContentLoaded", function () {
  document.addEventListener("click", function (e) {
    const addBag = e.target.closest(".tm-add-bag");
    if (!addBag) return;

    const form = addBag.closest("form");
    if (!form || form.classList.contains("animating")) return;

    // Initial state - start with loading
    form.classList.add("animating");
    addBag.classList.add("loading");

    // After a brief delay, add the full class for the gradient animation
    setTimeout(() => {
      addBag.classList.add("full");
    }, 100);

    // Handle the AJAX cart add
    setTimeout(async function () {
      const sectionsToBundle = ["variant-added"];
      const formData = new FormData(form);
      formData.set("sections", sectionsToBundle.join(","));

      const variantInput = form.querySelector('input[name="id"]');
      const variantId = variantInput ? variantInput.value : "";
      formData.set("sections_url", `${Shopify.routes.root}variants/${variantId}`);

      try {
        const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: "POST",
          body: formData,
          headers: {
            "X-Requested-With": "XMLHttpRequest"
          },
          credentials: "same-origin"
        });

        const responseJson = await response.json();

        if (response.ok) {
          // Success state
          addBag.classList.remove("loading");
          addBag.classList.add("success");
          const addText = addBag.querySelector(".tm-add-text");
          if (addText) {
            addText.textContent = "Added";
          }
          const themeVars = window.themeVariables || { settings: {} };
          if (themeVars.settings.cartType === "page" || themeVars.settings.pageType === "cart") {
            window.location.href = `${Shopify.routes.root}cart`;
            return; // Exit early if redirecting
          }

          const cartResponse = await fetch(`${Shopify.routes.root}cart.js`);
          const cartContent = await cartResponse.json();
          cartContent["sections"] = responseJson["sections"];

          // Trigger events
          form.dispatchEvent(new CustomEvent("variant:add", {
            bubbles: true,
            detail: {
              items: responseJson.items ? responseJson.items : [responseJson],
              cart: cartContent
            }
          }));

          document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
            bubbles: true,
            detail: {
              baseEvent: "variant:add",
              cart: cartContent
            }
          }));

          setTimeout(() => {
            document.dispatchEvent(new CustomEvent("cart:refresh"));
          }, 1000);

          // Reset after success animation completes
          setTimeout(() => {
            form.classList.remove("animating");
            addBag.classList.remove("full", "success", "loading");
          }, 2000);

        } else {
          // Error state - reset immediately
          addBag.classList.remove("loading", "full");
          form.classList.remove("animating");

          form.dispatchEvent(new CustomEvent("cart:error", {
            bubbles: true,
            detail: { error: responseJson.description }
          }));
          document.dispatchEvent(new CustomEvent("cart:refresh"));
        }
      } catch (error) {
        // Handle any AJAX errors
        console.error("Add to cart error:", error);
        addBag.classList.remove("loading", "full");
        form.classList.remove("animating");
      }
    }, 600);
  });
});


/* ----------------------------------------------------------------------------
   5. Swatch change -> variant name label
   When a swatch radio changes on a product card, update the visible
   ".variant-name-display" text to the chosen option name.
   -------------------------------------------------------------------------- */
document.addEventListener("change", function (e) {
  const radio = e.target;
  if (radio.type === "radio" && radio.name === "id") {
    const variantName = radio.getAttribute("data-variant-option1");
    const productCard = radio.closest("product-card");
    if (productCard) {
      const variantDisplay = productCard.querySelector(".variant-name-display");
      if (variantDisplay && variantName && variantName !== "Default Title") {
        variantDisplay.textContent = variantName;
      }
    }
  }
});


/* ----------------------------------------------------------------------------
   6. window.showVariantMediaInGallery (swatch hover -> gallery image)
   MUST stay on `window` - it is called from inline onclick/markup elsewhere.
   -------------------------------------------------------------------------- */
window.showVariantMediaInGallery = function (productId, sectionId, posIndex) {
  var galleryId = "product-gallery-" + productId + "-" + sectionId;
  var carousel = document.getElementById(galleryId);
  if (carousel && typeof carousel.select === "function") {
    carousel.select(posIndex, { animate: false });
  }
};


/* ----------------------------------------------------------------------------
   7. Cart recommendations carousel
   Custom element + its prev/next buttons for the recs row in the cart drawer.
   NOTE: sections/cart-drawer.liquid renders an inline TWIN of this block on
   every page. The customElements.get() guards below and the `initialized`
   flags on both cart-drawer:open listeners let the two copies coexist; the
   whole-file IIFE (see header) keeps their class names from colliding.
   -------------------------------------------------------------------------- */
class CartRecsCarousel extends HTMLElement {
  constructor() {
    super();
    this.scrollStep = 300; // Adjust based on your card width
  }

  connectedCallback() {
    const carouselId = this.id;
    this.prevButton = document.querySelector(`button[aria-controls="${carouselId}"][is="cart-recs-prev"]`);
    this.nextButton = document.querySelector(`button[aria-controls="${carouselId}"][is="cart-recs-next"]`);

    if (this.prevButton) {
      this.prevButton.addEventListener("click", (e) => this.handleNavClick(e, "prev"));
    }
    if (this.nextButton) {
      this.nextButton.addEventListener("click", (e) => this.handleNavClick(e, "next"));
    }

    this.style.overflowX = "auto";
    this.style.scrollBehavior = "smooth";
    this.addEventListener("scroll", () => this.updateButtonStates());
    this.updateButtonStates();

    // Defer update to allow content to render
    setTimeout(() => {
      this.updateButtonStates();
    }, 300);
  }

  handleNavClick(e, direction) {
    e.preventDefault();
    const scrollAmount = direction === "next" ? this.scrollStep : -this.scrollStep;
    this.scrollBy({ left: scrollAmount, behavior: "smooth" });
  }

  updateButtonStates() {
    if (!this.prevButton || !this.nextButton) return;
    this.prevButton.toggleAttribute("disabled", this.scrollLeft <= 0);
    const maxScroll = this.scrollWidth - this.clientWidth;
    this.nextButton.toggleAttribute("disabled", this.scrollLeft >= maxScroll);
  }
}

// Register custom elements (guarded so a double-load can't throw)
if (!window.customElements.get("cart-recs-carousel")) {
  window.customElements.define("cart-recs-carousel", CartRecsCarousel);
}
if (!window.customElements.get("cart-recs-prev")) {
  window.customElements.define("cart-recs-prev", class extends HTMLButtonElement {}, { extends: "button" });
}
if (!window.customElements.get("cart-recs-next")) {
  window.customElements.define("cart-recs-next", class extends HTMLButtonElement {}, { extends: "button" });
}

// Initialize carousels when the cart drawer opens
document.addEventListener("cart-drawer:open", () => {
  document.querySelectorAll("cart-recs-carousel").forEach((carousel) => {
    if (!carousel.initialized) {
      carousel.initialized = true;
      new CartRecsCarousel().connectedCallback.call(carousel);
    }
  });
});

// Re-initialize when a section reloads (theme editor only)
if (Shopify.designMode) {
  document.addEventListener("shopify:section:select", () => {
    setTimeout(() => {
      document.querySelectorAll("cart-recs-carousel").forEach((carousel) => {
        new CartRecsCarousel().connectedCallback.call(carousel);
      });
    }, 300);
  });
}


/* ----------------------------------------------------------------------------
   8. Sold-out product card fade
   Fades/greys product cards that carry a sold-out badge.
   (Switch to OPTION A inside if you'd rather hide them entirely.)
   -------------------------------------------------------------------------- */
document.addEventListener("DOMContentLoaded", function () {
  const soldOutBadges = document.querySelectorAll("sold-out-badge, .badge--sold-out");
  soldOutBadges.forEach(function (badge) {
    const productCard = badge.closest("product-card, .product-card");
    if (productCard) {
      // OPTION A: Completely hide the card
      // productCard.style.display = 'none';

      // OPTION B: Fade out the card (default)
      productCard.style.opacity = "0.4";
      productCard.style.filter = "grayscale(50%)";
    }
  });
});


/* ----------------------------------------------------------------------------
   9. Tepo customizer widget mover
   The Tepo app embed injects its widget at the bottom of the page; this moves
   it up next to the buy buttons. Self-disconnecting observer (max 10s).
   -------------------------------------------------------------------------- */
(function () {
  function moveTepo() {
    var widget = document.querySelector(".tcustomizer-widget");
    if (!widget) return false;
    var buyBlock = document.querySelector('[data-block-type="buy-buttons"]');
    if (!buyBlock) return false;
    if (widget.parentNode === buyBlock.parentNode && widget.nextElementSibling === buyBlock) return true;
    buyBlock.parentNode.insertBefore(widget, buyBlock);
    return true;
  }
  if (!moveTepo()) {
    var obs = new MutationObserver(function () {
      if (moveTepo()) obs.disconnect();
    });
    obs.observe(document.body, { childList: true, subtree: true });
    setTimeout(function () { obs.disconnect(); }, 10000);
  }
})();

/* ----------------------------------------------------------------------------
   10. Duo pair integrity (cart-wide orphan enforcement + load sweep)

   Duo planners are two Shopify products added together by the duo customizer
   (sections/duo-customizer.liquid). Each book line carries properties
   { _bundle: "Duo Bundle", "Duo Pair": "Duo N" }; accessory lines carry
   _duo_accessory and auto-remove with their parent book (parent_id).

   This block is the ONLY home of pair enforcement. The copy that used to sit
   inside duo-customizer.liquid ran only on customizer pages, so removing one
   book from the cart page (a full-reload /cart/change link, no JS event) or
   from the drawer on any other page orphaned its partner — three orders
   shipped as lone halves before this was caught (#75620, 2026-08-05).

   Two mechanisms, deliberately redundant:
     a) cart:change listener — covers the theme's ajax cart updates.
     b) page-load sweep — covers changes no event reaches: the cart page's
        full-reload remove links, other tabs, and partial adds (Shopify's
        multi-item /cart/add.js is NOT atomic: on 422 it can keep book 2;
        the customizer rolls those back itself, this is the safety net).
   After removing an orphan we reload on the cart page (server-rendered
   markup must be rebuilt) and dispatch cart:refresh everywhere else. The
   reload cannot loop: it only fires after a confirmed OK removal, and the
   next sweep finds no orphans.
   -------------------------------------------------------------------------- */
(function () {
  function shopRoot() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
  }

  function onCartPage() {
    return location.pathname.indexOf(shopRoot() + "cart") === 0;
  }

  function findOrphanKeys(cart) {
    var pairBooks = {};
    cart.items.forEach(function (item) {
      var props = item.properties || {};
      if (props["Duo Pair"] && !props["_duo_accessory"]) {
        (pairBooks[props["Duo Pair"]] = pairBooks[props["Duo Pair"]] || []).push(item);
      }
    });
    var keys = [];
    for (var pair in pairBooks) {
      if (pairBooks[pair].length === 1) keys.push(pairBooks[pair][0].key);
    }
    return keys;
  }

  async function removeOrphans(keys) {
    var updates = {};
    keys.forEach(function (k) { updates[k] = 0; });
    var response = await fetch(shopRoot() + "cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ updates: updates })
    });
    if (!response.ok) return; // never reload on a failed removal (would loop)
    if (onCartPage()) {
      location.reload();
    } else {
      document.dispatchEvent(new CustomEvent("cart:refresh"));
    }
  }

  /* a) ajax cart changes — same guard flag the old section copy used, so a
     stale-cached customizer page can't double-bind alongside this listener */
  if (!window.__duoBundleEnforcer) {
    window.__duoBundleEnforcer = true;
    document.documentElement.addEventListener("cart:change", async function (event) {
      try {
        var cart = event.detail && event.detail.cart;
        if (!cart) return;
        var orphans = findOrphanKeys(cart);
        if (orphans.length) await removeOrphans(orphans);
      } catch (err) {
        console.error("[custom.js] duo pair enforcement (cart:change) failed:", err);
      }
    });
  }

  /* b) page-load sweep */
  if (!window.__duoOrphanSweep) {
    window.__duoOrphanSweep = true;
    var sweep = async function () {
      try {
        var cart = await (await fetch(shopRoot() + "cart.js")).json();
        var orphans = findOrphanKeys(cart);
        if (orphans.length) await removeOrphans(orphans);
      } catch (err) {
        console.error("[custom.js] duo orphan sweep failed:", err);
      }
    };
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", function () { sweep(); });
    } else {
      sweep();
    }
  }
})();

})();
